// REACT IMPORTS
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

// COMPONENT IMPORTS
import Button from "components/form/Button";
import { InfoCircleIcon } from "icons/warning/InfoCircleIcon";
import { otpVerify, passwordForgot } from "store/slices/authSlice";
import { useAppDispatch } from "store/hooks";
import { decode, obscureEmail } from "utils/HelperFunctions";
import { useTid } from "utils/testid";
import { activateOnKey } from "utils/activateOnKey";

/* ------------------------------------- OTP VERIFICATION FORM ------------------------------------- */

const ForgotOtpVerificationForm = () => {
  const tid = useTid();
  const { state } = useLocation();
  const COUNTDOWN = 60;

  const dispatch = useAppDispatch();
  let navigate = useNavigate();
  const { clusterId: cluster_id } = useParams();
  const clusterId = decode(cluster_id || "", true);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [valid, setValid] = useState<boolean>(false);
  const [seconds, setSeconds] = useState<number>(COUNTDOWN);
  const [message, setMessage] = useState<string>(
    "The OTP you entered is invalid. Please try again."
  );
  const [otp, setOtp] = useState<string[]>(["", "", "", "", "", ""]);
  const [wrongOtp, setWrongOtp] = useState<boolean>(false);
  // Token returned by OTP verification; it authorises the password reset.
  const [otpToken, setOtpToken] = useState<string>("");

  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (seconds > 0) {
        setSeconds(seconds - 1);
      }
    }, 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seconds]);

  useEffect(() => {
    if (otp.every((char) => char.trim() !== "")) {
      onSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const value = e.target.value;
    if (!/^\d*$/.test(value) || value.length > 1) return; // Allow only numeric values

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (index < 6 - 1 && value !== "") {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace" && index > 0 && !otp[index]) {
      setWrongOtp(false);
      const newOtp = [...otp];
      newOtp[index - 1] = "";
      setOtp(newOtp);
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault(); // Prevent the default paste behavior

    const pastedText = e.clipboardData.getData("text");

    if (/^\d{6}$/.test(pastedText)) {
      // Check if the pasted text is a 6-digit OTP
      const newOtp = pastedText?.split("");
      setOtp(newOtp);

      // Focus on the first input field
      inputRefs.current[0]?.focus();
    }
  };

  const onSubmit = async () => {
    setIsLoading(true);
    try {
      let payload = {
        otp: otp?.join(""),
        accounts_id: state?.data?.accounts_id,
      };
      let res: any = await dispatch(otpVerify(payload)).unwrap();
      if (res?.body?.success) {
        const verifiedToken = res?.body?.token || "";
        setOtpToken(verifiedToken);
        setValid(true);
        setTimeout(() => {
          navigate(`/${cluster_id}/reset-password`, {
            state: {
              accounts_id: state?.data,
              otpToken: verifiedToken,
            },
          });
        }, 2000);
      } else {
        setMessage(
          res?.body?.data?.msg ||
            "The OTP you entered is invalid. Please try again."
        );
        setWrongOtp(true);
      }
    } catch (error: any) {
      console.log("otp verify err: -->", error?.message);
    } finally {
      setIsLoading(false);
    }
  };
  const onResendOtp = async () => {
    setWrongOtp(false);
    setOtp(["", "", "", "", "", ""]);
    setSeconds(COUNTDOWN);
    inputRefs.current[0]?.focus();
    try {
      let payload: any = {
        email: state?.email,
        cluster_id: clusterId,
        accountId: state?.data?.accountid,
        isEmailMutiple: state?.isEmailMutiple,
      };
      await dispatch(passwordForgot(payload)).unwrap();
    } catch (error: any) {
      console.log("send verification code error: -->", error?.message);
    }
  };

  const canResend = wrongOtp || seconds === 0;

  return (
    <div
      className="animate__animated animate__fadeIn w-full lg:max-w-[520px]"
      data-testid={tid("form-shell")}
    >
      <div className="lg:text-3xl text-xl font-bold -tracking-[0.6px] leading-normal text-txtPrimary">
        OTP Verification
      </div>
      <div className="lg:pt-[74px] pt-6">
        <p className="lg:text-base text-sm leading-normal font-normal text-txtParagraph">
          Enter the code we sent to
          <span className="font-semibold">
            &nbsp;
            {obscureEmail(state?.email)}
          </span>
        </p>
        <div
          className="flex justify-between w-full lg:max-w-[520px] pt-10"
          data-testid={tid("code-input-group")}
          aria-label="Password reset verification code"
        >
          {otp.map((value, index) => (
            <input
              key={`forgot-otp-digit-${index + 1}`}
              ref={(el) => (inputRefs.current[index] = el)}
              type="number"
              inputMode="numeric"
              value={value}
              onPaste={(e) => handlePaste(e)}
              onChange={(e) => handleChange(e, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              maxLength={1}
              autoFocus={index === 0}
              data-testid={tid(`code-digit-${index + 1}-input`)}
              aria-label={`Password reset verification code digit ${index + 1}`}
              className="w-10 h-10 lg:w-[60px] lg:h-[60px] sm:w-[50px] sm:h-[50px] text-center focus:outline-none bg-bgSecondary rounded-[6px] lg:text-3xl text-xl leading-normal font-medium text-txtPrimary"
            />
          ))}
        </div>
        {wrongOtp ? (
          <div className="pt-4">
            <div
              className="px-2.5 py-3 bg-lightRed rounded flex items-center gap-[5px] lg:max-w-[520px] w-full"
              role="alert"
              data-testid={tid("invalid-code-alert")}
            >
              <InfoCircleIcon
                strokeColor="var(--color-red)"
                aria-hidden="true"
              />
              <p className="text-red text-xs leading-normal">{message}</p>
            </div>
          </div>
        ) : null}
      </div>
      <div className="pt-10 flex flex-col items-center w-full lg:max-w-[520px] gap-2">
        <div className="flex flex-col items-center lg:gap-5 gap-4">
          {!wrongOtp ? (
            <div
              className="text-btnPrimaryActive lg:text-lg text-base font-normal leading-normal"
              data-testid={tid("resend-countdown")}
              aria-label={`Resend available in ${seconds} seconds`}
            >
              {`${Math.floor(seconds / 60)
                .toString()
                .padStart(2, "0")}:${(seconds % 60)
                .toString()
                .padStart(2, "0")}`}
            </div>
          ) : null}
          <div className="text-txtSecondary font-medium lg:text-base text-sm leading-normal opacity-40">
            Didn’t receive the code?
          </div>
        </div>
        <div
          className={`lg:text-base text-sm font-medium leading-normal ${
            canResend
              ? "text-btnPrimaryActive cursor-pointer"
              : "text-strokeActive pointer-events-none"
          }`}
          role="button"
          tabIndex={canResend ? 0 : -1}
          aria-disabled={!canResend}
          aria-label="Resend password reset code"
          data-testid={tid("resend-code-button")}
          onClick={() => canResend && onResendOtp()}
          onKeyDown={activateOnKey(() => canResend && onResendOtp())}
        >
          Resend code
        </div>
      </div>
      <div className="lg:max-w-[520px] lg:pt-[66px] pt-10">
        <Button
          isLoading={isLoading}
          className="w-full"
          text="Next"
          data-testid={tid("next-button")}
          aria-label="Continue to reset password"
          isDisabled={!valid}
          variant="variable"
          icon={valid ? "SuccessTick" : null}
          onClick={() => {
            navigate(`/${cluster_id}/reset-password`, {
              state: {
                accounts_id: state?.data,
                otpToken,
              },
            });
          }}
        />
        <div className="pt-4 lg:text-base text-sm text-blue hover:!text-btnPrimaryHover leading-normal font-medium flex justify-center">
          <div
            className="cursor-pointer"
            onClick={() => navigate(-1)}
            onKeyDown={activateOnKey(() => navigate(-1))}
            role="button"
            tabIndex={0}
            data-testid={tid("go-back-button")}
            aria-label="Go back"
          >
            Go back
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotOtpVerificationForm;
